import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Flame, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "../lib/seo";
import { Wordmark } from "../components/Wordmark";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  useSeo({ title: "Choose a new password", description: "Reset your First Step Fire Prep password.", path: "/reset-password", noindex: true });
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Those passwords don't match."); return; }
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setUser(data);
      toast.success("Password updated — you're logged in.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center"><Flame className="w-5 h-5 text-white" /></div>
          <Wordmark />
        </Link>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Choose a new password</h1>
        <p className="mt-2 text-slate-500">At least 6 characters. You'll be logged in right after.</p>
        {!token ? (
          <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-slate-700" data-testid="reset-password-no-token">
            This link is missing its reset code. <Link to="/auth" className="text-primary font-semibold hover:underline">Request a new one</Link>.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-8" data-testid="reset-password-form">
            <div>
              <Label htmlFor="new-password">New password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="••••••••" className="pl-10 h-12" data-testid="reset-password-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} placeholder="••••••••" className="pl-10 h-12" data-testid="reset-password-confirm-input" />
              </div>
            </div>
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="reset-password-error">
                {error} {/expired|invalid/i.test(error) && <Link to="/auth" className="font-semibold underline">Request a new link</Link>}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-12 bg-primary hover:bg-primary/90 font-semibold" data-testid="reset-password-submit">
              {loading ? "Please wait…" : "Set new password"}{!loading && <ArrowRight className="ml-2 w-4 h-4" />}
            </Button>
            <p className="text-center text-sm text-slate-500"><Link to="/auth" className="text-primary font-semibold hover:underline">Back to log in</Link></p>
          </form>
        )}
      </div>
    </div>
  );
}
